import React, { useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Database, ArrowRight, Play, RefreshCw, Sparkles } from 'lucide-react';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { StatusChip } from '@/components/shared/StatusChip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PORTFOLIOS, CLIENTS } from '@/data/seed';
import { formatSAR, formatNumber, formatPct } from '@/lib/format';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePageTitle } from '@/lib/usePageTitle';

export default function Portfolios() {
  usePageTitle('Portfolios');
  const navigate = useNavigate();
  const { pushEvent } = useApp();
  const [validating, setValidating] = useState(false);
  const [validationStep, setValidationStep] = useState(0);
  const [imported, setImported] = useState(false);

  const runImport = () => {
    toast.message('ERP import started', { description: 'Pulling overdue accounts from Saudi Retail Bank — Synthetic' });
    pushEvent({ type: 'ERPImportBatch', src: 'Client ERP', dst: 'Alwasl Recovery OS', status: 'success', payload: '1,284 accounts queued', caseId: '—' });
    setTimeout(() => {
      setImported(true);
      toast.success('1,284 accounts imported', { description: 'Awaiting validation • PRT-2026-019' });
    }, 900);
  };

  const runValidation = () => {
    setValidating(true); setValidationStep(0);
    const steps = ['Schema check', 'ID / Iqama format', 'Mobile quality', 'Duplicate detection', 'Amount consistency', 'Legal flags'];
    let i = 0;
    const id = setInterval(() => {
      i++; setValidationStep(i);
      if (i >= steps.length) {
        clearInterval(id);
        setValidating(false);
        toast.success('Portfolio validated', { description: '92/100 health score • 18 duplicates resolved' });
        pushEvent({ type: 'PortfolioValidated', src: 'Alwasl Recovery OS', dst: 'Audit', status: 'success', payload: 'PRT-2026-019 health 92' });
      }
    }, 380);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow="Portfolio intake"
        title="Ingest → Validate → Enrich → Ready to assign"
        subtitle="Pull overdue accounts from client ERP/core systems, validate data quality, enrich with mock SIMAH credit indicators, then route to the assignment engine."
        actions={(
          <>
            <Button variant="outline" size="sm"><Upload className="h-4 w-4 mr-1.5" />Upload CSV</Button>
            <Button size="sm" className="bg-secondary text-secondary-foreground hover:bg-secondary/90" onClick={runImport}>
              <Database className="h-4 w-4 mr-1.5" /> Import from mock ERP
            </Button>
          </>
        )}
      />

      {/* Two-column intake panel */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Active import card */}
        <div className="surface-card p-5 lg:col-span-2 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">New import</p>
              <h3 className="text-lg font-semibold mt-0.5">{imported ? 'PRT-2026-019 — Saudi Retail Bank' : 'No active import'}</h3>
            </div>
            {imported && <Badge variant="outline" className="text-success border-success/40 bg-success/10">1,284 accounts</Badge>}
          </div>

          {!imported ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-border rounded-xl bg-muted/40">
              <FileSpreadsheet className="h-9 w-9 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">Drag CSV here or trigger a mock ERP pull to begin.</p>
              <Button size="sm" onClick={runImport} className="mt-3 bg-primary text-primary-foreground hover:bg-primary/90">
                <Play className="h-4 w-4 mr-1.5" /> Trigger mock ERP pull
              </Button>
            </div>
          ) : (
            <div className="flex-1 flex flex-col">
              {/* Validation steps */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                {['Schema check', 'ID / Iqama format', 'Mobile quality', 'Duplicate detection', 'Amount consistency', 'Legal flags'].map((s, i) => {
                  const done = validationStep > i || (!validating && validationStep === 6);
                  const active = validating && validationStep === i;
                  return (
                    <div key={s} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${done ? 'border-success/40 bg-success/10 text-success' : active ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-muted text-muted-foreground'}`}>
                      {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : active ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <span className="h-2 w-2 rounded-full bg-current opacity-30" />}
                      {s}
                    </div>
                  );
                })}
              </div>

              {/* Issues found */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { k: 'Accounts', v: 1284, cls: 'text-foreground' },
                  { k: 'Validated', v: 1242, cls: 'text-success' },
                  { k: 'Duplicates', v: 18, cls: 'text-warning' },
                  { k: 'Missing phone', v: 24, cls: 'text-destructive' },
                ].map((s) => (
                  <div key={s.k} className="p-3 rounded-lg border border-border bg-muted/40">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.k}</p>
                    <p className={`font-mono-num text-xl font-semibold mt-1 ${s.cls}`}>{formatNumber(s.v)}</p>
                  </div>
                ))}
              </div>

              <div className="mt-auto flex flex-wrap gap-2 pt-3 border-t border-border">
                <Button size="sm" onClick={runValidation} disabled={validating} className="bg-primary text-primary-foreground hover:bg-primary/90">
                  {validating ? <><RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />Validating…</> : <><Sparkles className="h-4 w-4 mr-1.5" />Run validation</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate('/app/assignments')}>
                  Approve for assignment <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* SIMAH enrichment preview */}
        <div className="surface-card p-5 flex flex-col">
          <p className="text-[10px] uppercase tracking-widest text-primary">SIMAH enrichment</p>
          <h3 className="text-base font-semibold mt-0.5 mb-3">Mock credit profile</h3>
          <div className="space-y-2.5 text-sm flex-1">
            {[
              { k: 'Credit risk band', v: 'B2 (medium)', tone: 'warning' },
              { k: 'Open obligations', v: 'SAR 142,300', tone: 'foreground' },
              { k: 'Recent enquiries', v: '3 in last 30 days', tone: 'foreground' },
              { k: 'Default flag', v: 'None', tone: 'success' },
              { k: 'Bureau response', v: '540 ms', tone: 'muted' },
            ].map((r) => (
              <div key={r.k} className="flex items-center justify-between">
                <span className="text-muted-foreground">{r.k}</span>
                <span className={`font-medium ${r.tone === 'warning' ? 'text-warning' : r.tone === 'success' ? 'text-success' : r.tone === 'muted' ? 'text-muted-foreground' : 'text-foreground'}`}>{r.v}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-[11px]">
            <span className="badge-mock">Mock sandbox</span>
            <span className="text-muted-foreground">Audit logged • PDPL safe</span>
          </div>
        </div>
      </div>

      {/* Existing portfolios list */}
      <div className="surface-card overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">All portfolios</p>
            <h3 className="text-base font-semibold mt-0.5">Active client books</h3>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{PORTFOLIOS.length} portfolios</span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>{CLIENTS.length} clients</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Portfolio</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Client</th>
                <th scope="col" className="text-right px-3 py-2.5 font-medium">Accounts</th>
                <th scope="col" className="text-right px-3 py-2.5 font-medium">Value</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Validation</th>
                <th scope="col" className="text-left px-3 py-2.5 font-medium">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {PORTFOLIOS.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-muted/40">
                  <td className="px-5 py-3 font-medium">{p.id}</td>
                  <td className="px-3 py-3 text-muted-foreground">{p.clientName}</td>
                  <td className="px-3 py-3 text-right font-mono-num">{formatNumber(p.accounts)}</td>
                  <td className="px-3 py-3 text-right font-mono-num">{formatSAR(p.value, { compact: true })}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <Progress value={(p.validated / p.accounts) * 100} className="h-1.5 w-24" />
                      <span className="text-[11px] font-mono-num text-muted-foreground">{formatPct((p.validated / p.accounts) * 100, 0)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3"><StatusChip status={p.status} /></td>
                  <td className="px-3 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => navigate('/app/portfolios')} data-testid={`portfolio-open-${p.id}`}>View portfolio</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
